import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSubmitCheckIn, useCheckInHistory } from '../api/checkIns';
import { applyServerErrors } from '../lib/formErrors';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import { FormPageSkeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '../lib/dates';

interface FormValues { completed: string; plannedNext: string; blockers: string; }

export default function CheckInPage() {
  const { currentUser } = useAuth();
  const { mutate, isPending } = useSubmitCheckIn();
  const { data: history, isLoading } = useCheckInHistory(currentUser!.id);

  const todayIso     = new Date().toISOString().slice(0, 10);
  const alreadyToday = history?.items.find((c) => c.date === todayIso);

  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm<FormValues>();

  const onSubmit = handleSubmit((values) => {
    mutate(
      { ...values, date: todayIso, blockers: values.blockers || undefined },
      {
        onSuccess: () => { toast.success('Check-in submitted.'); reset(); },
        onError:   (e) => applyServerErrors(e, setError),
      },
    );
  });

  if (isLoading) return <FormPageSkeleton fields={3} />;

  if (alreadyToday) {
    return (
      <div className="max-w-lg">
        <PageHeader title="Daily check-in" />
        <Card className="overflow-hidden">
          {/* Success header */}
          <div className="flex flex-col items-center border-b border-border px-5 py-8 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/40">
              <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="font-semibold text-foreground">All checked in for today</p>
            <p className="mt-1 text-sm text-muted-foreground">{formatDate(todayIso)}</p>
          </div>

          {/* Completed */}
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-50 dark:bg-emerald-950/40">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-semibold text-foreground">Completed</p>
          </div>
          <div className="px-5 py-4">
            <p className="whitespace-pre-wrap text-sm text-foreground">{alreadyToday.completed}</p>
          </div>

          {/* Planned next */}
          <div className="flex items-center gap-2.5 border-y border-border px-5 py-4">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
              <ArrowRight className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Planned next</p>
          </div>
          <div className="px-5 py-4">
            <p className="whitespace-pre-wrap text-sm text-foreground">{alreadyToday.plannedNext}</p>
          </div>

          {/* Blockers */}
          {alreadyToday.blockers && (
            <>
              <div className="flex items-center gap-2.5 border-y border-border px-5 py-4">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-50 dark:bg-amber-950/40">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                </div>
                <p className="text-sm font-semibold text-foreground">Blockers</p>
              </div>
              <div className="px-5 py-4">
                <p className="whitespace-pre-wrap text-sm text-foreground">{alreadyToday.blockers}</p>
              </div>
            </>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-lg">
      <PageHeader
        title="Daily check-in"
        description="Takes under 60 seconds. Replaces the stand-down meeting."
      />

      <Card className="overflow-hidden">
        <form onSubmit={onSubmit}>
          {/* Completed */}
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-50 dark:bg-emerald-950/40">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-semibold text-foreground">What did you complete?</p>
            <span className="ml-auto text-xs text-destructive">required</span>
          </div>
          <div className="px-5 py-5">
            <Textarea
              rows={3}
              {...register('completed', { required: 'Required' })}
              placeholder="Summarise what you finished today or since last check-in."
            />
            {errors.completed && <p className="mt-1.5 text-xs text-destructive">{errors.completed.message}</p>}
          </div>

          {/* Planned next */}
          <div className="flex items-center gap-2.5 border-y border-border px-5 py-4">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
              <ArrowRight className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">What are you working on next?</p>
            <span className="ml-auto text-xs text-destructive">required</span>
          </div>
          <div className="px-5 py-5">
            <Textarea
              rows={3}
              {...register('plannedNext', { required: 'Required' })}
              placeholder="What do you plan to do until your next check-in?"
            />
            {errors.plannedNext && <p className="mt-1.5 text-xs text-destructive">{errors.plannedNext.message}</p>}
          </div>

          {/* Blockers */}
          <div className="flex items-center gap-2.5 border-y border-border px-5 py-4">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-50 dark:bg-amber-950/40">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-sm font-semibold text-foreground">Any blockers?</p>
            <span className="ml-auto text-xs text-muted-foreground">optional</span>
          </div>
          <div className="px-5 py-5">
            <Textarea
              rows={2}
              {...register('blockers')}
              placeholder="Anything preventing you from making progress?"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center gap-3 border-t border-border bg-muted/30 px-5 py-4">
            <Button type="submit" loading={isPending}>Submit check-in</Button>
            <span className="text-xs text-muted-foreground">{formatDate(todayIso)}</span>
          </div>
        </form>
      </Card>
    </div>
  );
}
